import { Button, Checkbox, LoadSpinner, Select, TextArea } from '@calizahq/react-components-v2'
import { Form, Formik } from 'formik'
import React, { useState } from 'react'

import { ScheduledCommunicationTemplateMutations } from '@/graphql/mutations/scheduledCommunicationTemplate.mutations'
import { useMobile } from '@/hooks'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import useMarketSegments from '@/hooks/useMarketSegments'
import useScheduledCommunicationTypes from '@/hooks/useScheduledCommunicationTypes/useScheduledCommunicationTypes'
import { NewScheduledCommunicationTemplateDetails } from '@/types/ScheduledCommunicationTemplate'

import { formSchema, FormValuesType, initialValues } from './CreateScheduledCommunicationTemplateForm.util'
import { CreateScheduledCommunicationTypeConfirmation } from './CreateScheduledCommunicationTemplateFormConfirmation'

type Props = {
  refetchOnSuccess?: () => void
  toggle: () => void
}

export const CreateScheduledCommunicationTemplateForm = ({ toggle, refetchOnSuccess }: Props) => {
  const [error, setError] = useState<string>()
  const [scheduledCommunicationTemplate, setScheduledCommunicationTemplate] =
    useState<NewScheduledCommunicationTemplateDetails>()

  const { data: marketSegmentData, isPending: isMarketSegmentsLoading } = useMarketSegments()
  const { data: scheduledCommunicationTypesData, isPending: isScheduledCommunicationTypesLoading } =
    useScheduledCommunicationTypes()

  const { isTablet } = useMobile()

  const marketSegmentOptions =
    marketSegmentData && marketSegmentData.marketSegments
      ? marketSegmentData.marketSegments.map((marketSegment) => {
          return { label: marketSegment.name, value: marketSegment.id.toString() }
        })
      : []
  marketSegmentOptions.unshift({ label: 'Select Market Segment', value: '' })

  const scheduledCommunicationTypeOptions =
    scheduledCommunicationTypesData && scheduledCommunicationTypesData.scheduledCommunicationTypes
      ? scheduledCommunicationTypesData.scheduledCommunicationTypes.map((scheduledCommunicationType) => {
          return { label: scheduledCommunicationType.description, value: scheduledCommunicationType.id.toString() }
        })
      : []
  scheduledCommunicationTypeOptions.unshift({ label: 'Select Type', value: '' })

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(
    ScheduledCommunicationTemplateMutations.createScheduledCommunicationTemplate(),
    {
      onSuccess: (res: { createScheduledCommunicationTemplate: NewScheduledCommunicationTemplateDetails }) => {
        setScheduledCommunicationTemplate(res.createScheduledCommunicationTemplate)
        refetchOnSuccess && refetchOnSuccess()
      },
      onError: (res: { response: { errors: { message: string }[] } }) => {
        const { response } = res
        if (response.errors && response.errors.length) {
          setError(response.errors[0].message)
        }
      },
    },
  )

  const handleSubmit = (values: FormValuesType) => {
    const { marketSegmentId, scheduledCommunicationTypeId, ...restOfValues } = values

    mutate({
      scheduledCommunicationTemplate: {
        marketSegmentId: Number(marketSegmentId),
        scheduledCommunicationTypeId: Number(scheduledCommunicationTypeId),
        ...restOfValues,
      },
    })
  }

  const isLoading = isMarketSegmentsLoading || isScheduledCommunicationTypesLoading

  return (
    <>
      {isLoading ? (
        <div className="flex h-48 w-full items-center justify-center ">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          {scheduledCommunicationTemplate ? (
            <CreateScheduledCommunicationTypeConfirmation
              scheduledCommunicationTemplate={scheduledCommunicationTemplate}
              toggle={toggle}
            />
          ) : (
            <Formik
              enableReinitialize={true}
              initialValues={{
                ...initialValues,
              }}
              onSubmit={handleSubmit}
              validateOnBlur={false}
              validationSchema={formSchema}
            >
              {() => {
                return (
                  <Form className="flex min-h-140 w-full flex-col flex-wrap px-6 md:max-w-160">
                    <p className="leading-nonemt-4 mb-4 font-bold text-header-md">
                      New Scheduled Communication Template
                    </p>

                    <Checkbox className="mb-4 w-full" id="active" label="Active?" name="active" />
                    <Select
                      className="mb-4 w-full"
                      id="marketSegmentId"
                      label="Market Segment"
                      name="marketSegmentId"
                      options={marketSegmentOptions}
                    />
                    <Select
                      className="mb-4 w-full"
                      id="scheduledCommunicationTypeId"
                      label="Scheduled Communication Type"
                      name="scheduledCommunicationTypeId"
                      options={scheduledCommunicationTypeOptions}
                    />
                    <TextArea className="mb-4 w-full h-fit-content" label="Content" maxLength={1000} name="content" />

                    {error && <p className=" mb-4 text-p-sm text-text-semantic-error">{error}</p>}
                    <div className="flex flex-col-reverse justify-between md:flex-row">
                      <Button
                        className="w-full bg-action-secondary md:flex md:w-auto"
                        onClick={toggle}
                        size={isTablet ? 'medium' : 'small'}
                        variant="secondary"
                      >
                        Close
                      </Button>
                      <Button
                        className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                        disabled={isMutationLoading}
                        isLoading={isMutationLoading}
                        size={isTablet ? 'medium' : 'small'}
                        type="submit"
                      >
                        Create
                      </Button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          )}
        </>
      )}
    </>
  )
}
